/**
 * /accounting/fixed-assets — Fixed Assets register + monthly depreciation runner.
 *
 * One-screen view of every capitalized asset, its book value, and a single
 * "Run depreciation" button that catches up every active asset to the
 * current month. Posting is idempotent — running it twice does nothing the
 * second time.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Play, Archive } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useFixedAssets, type FixedAsset } from "@/data/accounting/fixedAssets/FixedAssetsContext";

const fmt = (n: number) => "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

const CATEGORIES: FixedAsset["category"][] = ["vehicle", "equipment", "tools", "leasehold_improvement", "other"];

export default function FixedAssetsPage() {
  const { assets, createAsset, disposeAsset, monthlyDepreciation, bookValue, postDepreciationThroughCurrentMonth } = useFixedAssets();
  const [open, setOpen] = useState(false);
  const [disposing, setDisposing] = useState<FixedAsset | null>(null);

  const totals = useMemo(() => ({
    cost: assets.filter(a => !a.disposedAt).reduce((s, a) => s + a.cost, 0),
    bookValue: assets.filter(a => !a.disposedAt).reduce((s, a) => s + bookValue(a), 0),
    monthly: assets.filter(a => !a.disposedAt).reduce((s, a) => s + monthlyDepreciation(a), 0),
  }), [assets, bookValue, monthlyDepreciation]);

  const runDepreciation = () => {
    const { posted, skipped } = postDepreciationThroughCurrentMonth();
    toast({
      title: posted.length ? `Posted ${posted.length} depreciation ${posted.length === 1 ? "entry" : "entries"}` : "Nothing to post",
      description: posted.length
        ? `Caught every active asset up to the current month.${skipped ? ` ${skipped} skipped.` : ""}`
        : "All active assets are already depreciated through this month.",
    });
  };


  return (
    <CockpitPage>
      <div className="mb-3 flex items-center justify-between gap-3">
        <Link to="/accounting" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Books
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={runDepreciation}>
            <Play className="h-3.5 w-3.5 mr-1.5" /> Run depreciation
          </Button>
          <NewAssetDialog open={open} onOpenChange={setOpen} onCreate={createAsset} />
        </div>
      </div>
      <BooksNavRail />

      <div className="mb-4">
        <h1 data-tour-id="fixed-assets-register" className="text-xl font-medium tracking-tight">Fixed Assets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Straight-line depreciation. Monthly JEs hit Depreciation Expense (6920) / Accumulated Depreciation (1590).
        </p>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card label="Total cost basis" value={fmt(totals.cost)} />
          <Card label="Net book value" value={fmt(totals.bookValue)} tone="primary" />
          <Card label="Monthly depreciation" value={fmt(totals.monthly)} />
        </div>
      </CockpitSection>

      <CockpitSection title="Register" className="mt-6">
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Asset</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">In service</th>
                <th className="text-right px-3 py-2">Cost</th>
                <th className="text-right px-3 py-2">Life</th>
                <th className="text-right px-3 py-2">Monthly</th>
                <th className="text-right px-3 py-2">Book value</th>
                <th className="text-right px-3 py-2">Last posted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-muted-foreground py-8">No assets yet. Click "Add asset" to capitalize a purchase.</td></tr>
              ) : assets.map(a => (
                <tr key={a.id} className={a.disposedAt ? "opacity-60" : ""}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{a.name}</div>
                    {a.notes && <div className="text-xs text-muted-foreground">{a.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground capitalize">{a.category.replace("_", " ")}</td>
                  <td className="px-3 py-2 text-muted-foreground">{fmtDate(a.inServiceDate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(a.cost)}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{a.lifeMonths} mo</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(monthlyDepreciation(a))}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(bookValue(a))}</td>
                  <td className="px-3 py-2 text-right">
                    {a.lastPostedPeriod
                      ? <Badge variant="outline" className="text-xs">{a.lastPostedPeriod}</Badge>
                      : <span className="text-xs text-muted-foreground">never</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.disposedAt ? (
                      <span className="text-[10px] text-muted-foreground">
                        Disposed {a.disposalDate ?? a.disposedAt.slice(0, 10)}
                        {a.disposalGainLoss != null && a.disposalGainLoss !== 0 && (
                          <span className={a.disposalGainLoss > 0 ? " text-emerald-600" : " text-destructive"}>
                            {" "}({a.disposalGainLoss > 0 ? "gain " : "loss "}{fmt(Math.abs(a.disposalGainLoss))})
                          </span>
                        )}
                      </span>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setDisposing(a)}>
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CockpitSection>

      <DisposeDialog
        asset={disposing}
        onClose={() => setDisposing(null)}
        onConfirm={(input) => {
          if (!disposing) return;
          try {
            const { journalEntry } = disposeAsset(disposing.id, input);
            toast({
              title: "Asset disposed",
              description: journalEntry
                ? `Posted ${journalEntry.number} — cost cleared, gain/loss recognized.`
                : "Recorded the disposal.",
            });
            setDisposing(null);
          } catch (e) {
            toast({
              title: "Couldn't dispose asset",
              description: e instanceof Error ? e.message : "Check the posting date isn't in a closed period.",
              variant: "destructive",
            });
          }
        }}
        bookValue={(asset, asOf) => bookValue(asset, asOf)}
      />
    </CockpitPage>
  );
}


function Card({ label, value, tone }: { label: string; value: string; tone?: "primary" }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={"mt-1 text-xl font-semibold tabular-nums " + (tone === "primary" ? "text-primary" : "")}>{value}</div>
    </div>
  );
}

function NewAssetDialog({ open, onOpenChange, onCreate }: {
  open: boolean; onOpenChange: (v: boolean) => void;
  onCreate: ReturnType<typeof useFixedAssets>["createAsset"];
}) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<FixedAsset["category"]>("equipment");
  const [cost, setCost] = useState("");
  const [salvage, setSalvage] = useState("0");
  const [life, setLife] = useState("60");
  const [inService, setInService] = useState(() => new Date().toISOString().slice(0, 10));

  const reset = () => { setName(""); setCategory("equipment"); setCost(""); setSalvage("0"); setLife("60"); setInService(new Date().toISOString().slice(0, 10)); };

  const submit = () => {
    try {
      onCreate({
        name, category,
        cost: Number(cost),
        salvageValue: Number(salvage) || 0,
        lifeMonths: Number(life),
        inServiceDate: inService,
      });
      toast({ title: "Asset added", description: `${name} will depreciate over ${life} months.` });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Couldn't add asset", description: e instanceof Error ? e.message : "Check the inputs.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add asset</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Capitalize an asset</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="fa-name">Name</Label>
            <Input id="fa-name" value={name} onChange={e => setName(e.target.value)} placeholder="2024 Ford Transit Van" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={v => setCategory(v as FixedAsset["category"])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="fa-date">In-service date</Label>
              <Input id="fa-date" type="date" value={inService} onChange={e => setInService(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="fa-cost">Cost</Label>
              <Input id="fa-cost" type="number" value={cost} onChange={e => setCost(e.target.value)} placeholder="42000" />
            </div>
            <div>
              <Label htmlFor="fa-salv">Salvage</Label>
              <Input id="fa-salv" type="number" value={salvage} onChange={e => setSalvage(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="fa-life">Life (months)</Label>
              <Input id="fa-life" type="number" value={life} onChange={e => setLife(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name || !cost || Number(cost) <= 0 || !life || Number(life) <= 0}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisposeDialog({
  asset, onClose, onConfirm, bookValue,
}: {
  asset: FixedAsset | null;
  onClose: () => void;
  onConfirm: (input: { reason: string; disposalDate: string; proceeds: number }) => void;
  bookValue: (asset: FixedAsset, asOf?: string) => number;
}) {
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [proceeds, setProceeds] = useState("0");

  // Reset state whenever a new asset opens the dialog.
  useMemo(() => {
    if (asset) {
      setReason("");
      setDate(new Date().toISOString().slice(0, 10));
      setProceeds("0");
    }
  }, [asset?.id]);

  const nbv = asset ? bookValue(asset, date) : 0;
  const proceedsNum = Number(proceeds) || 0;
  const gainLoss = Math.round((proceedsNum - nbv) * 100) / 100;

  return (
    <Dialog open={!!asset} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Dispose {asset?.name}</DialogTitle></DialogHeader>
        {asset && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="disp-date">Disposal date</Label>
                <Input id="disp-date" type="date" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="disp-proc">Cash proceeds</Label>
                <Input id="disp-proc" type="number" min="0" step="0.01" value={proceeds}
                  onChange={e => setProceeds(e.target.value)} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label htmlFor="disp-reason">Reason</Label>
              <Input id="disp-reason" value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Sold, scrapped, traded-in…" />
            </div>
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1.5">
              <Row label="Net book value (as of disposal)" value={fmt(nbv)} />
              <Row label="Cash proceeds" value={fmt(proceedsNum)} />
              <div className="border-t border-border my-1" />
              <Row
                label={gainLoss >= 0 ? "Gain on disposal (→ 4995)" : "Loss on disposal (→ 6995)"}
                value={fmt(Math.abs(gainLoss))}
                tone={gainLoss >= 0 ? "good" : "bad"}
              />
              <p className="text-[10px] text-muted-foreground pt-1">
                Posts: DR Cash {proceedsNum > 0 ? fmt(proceedsNum) : "—"} · DR Accum. Dep. {fmt(asset.cost - nbv)} ·
                {gainLoss < 0 ? ` DR Loss ${fmt(Math.abs(gainLoss))} ·` : ""} CR Asset cost {fmt(asset.cost)}
                {gainLoss > 0 ? ` · CR Gain ${fmt(gainLoss)}` : ""}.
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onConfirm({ reason: reason.trim(), disposalDate: date, proceeds: proceedsNum })}
            disabled={!reason.trim() || !date}
          >
            Confirm disposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={
        "tabular-nums font-medium " +
        (tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-destructive" : "")
      }>{value}</span>
    </div>
  );
}

